import type { GameId } from '../api/types';
import { DEFAULT_GAME_ID, storedGameId } from '../data/games';
import { normalizeDrawDate } from '../utils/ticketMatch';

export type TicketSource = 'ocr' | 'llm' | 'manual';

export interface ScannedTicket {
  id: string;
  gameId?: GameId;
  main: number[];
  bonus: number[];
  drawDate?: string;
  cost?: number;
  status: 'pending' | 'lost' | 'won-small' | 'jackpot' | 'unknown-draw';
  prize?: number;
  scannedAt: string;
  source: TicketSource;
  confidence?: number;
  match?: {
    main: number[];
    bonus: number[];
  };
}

export function safeParseStoredTickets(raw: unknown): ScannedTicket[] {
  if (!Array.isArray(raw)) return [];
  const out: ScannedTicket[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const t = entry as Partial<ScannedTicket>;
    if (
      typeof t.id !== 'string' ||
      !Array.isArray(t.main) ||
      !Array.isArray(t.bonus) ||
      typeof t.scannedAt !== 'string' ||
      typeof t.source !== 'string'
    ) {
      continue;
    }
    const main = t.main.filter(
      (n): n is number => typeof n === 'number' && Number.isFinite(n)
    );
    const bonus = t.bonus.filter(
      (n): n is number => typeof n === 'number' && Number.isFinite(n)
    );
    const source = ['ocr', 'llm', 'manual'].includes(t.source)
      ? (t.source as TicketSource)
      : 'manual';
    const status = typeof t.status === 'string'
      ? (t.status as ScannedTicket['status'])
      : 'unknown-draw';
    const parsedGameId = storedGameId(t.gameId);
    const match = parseStoredMatch((t as { match?: unknown }).match);
    out.push({
      id: t.id,
      gameId: parsedGameId,
      main,
      bonus,
      drawDate: typeof t.drawDate === 'string' ? t.drawDate : undefined,
      cost: typeof t.cost === 'number' ? t.cost : undefined,
      status,
      prize: typeof t.prize === 'number' ? t.prize : undefined,
      scannedAt: t.scannedAt,
      source,
      confidence: typeof t.confidence === 'number' ? t.confidence : undefined,
      match,
    });
  }
  return out;
}

function parseStoredMatch(raw: unknown): ScannedTicket['match'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const match = raw as { main?: unknown; bonus?: unknown };
  const main = Array.isArray(match.main)
    ? match.main.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
    : [];
  const bonus = Array.isArray(match.bonus)
    ? match.bonus.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
    : [];
  if (main.length === 0 && bonus.length === 0) return undefined;
  return { main, bonus };
}

/** Stable dedupe key: game + sorted mains + sorted bonus + normalised draw date. */
export function dedupeKey(
  main: number[],
  bonus: number[],
  drawDate?: string,
  gameId?: GameId
): string {
  const m = [...main].sort((a, b) => a - b).join(',');
  const b = [...bonus].sort((a, b) => a - b).join(',');
  return `${gameId ?? DEFAULT_GAME_ID}|${m}|${b}|${normalizeDrawDate(drawDate)}`;
}

export function ticketMatchesGame(ticket: ScannedTicket, gameId: GameId): boolean {
  return (ticket.gameId ?? DEFAULT_GAME_ID) === gameId;
}

export function mergeById(a: ScannedTicket[], b: ScannedTicket[]): ScannedTicket[] {
  const byId = new Map<string, ScannedTicket>();
  for (const t of a) byId.set(t.id, t);
  for (const t of b) {
    const existing = byId.get(t.id);
    if (!existing) {
      byId.set(t.id, t);
      continue;
    }
    byId.set(t.id, existing.scannedAt >= t.scannedAt ? existing : t);
  }
  return Array.from(byId.values()).sort((x, y) =>
    x.scannedAt < y.scannedAt ? 1 : -1
  );
}

export function genId(): string {
  const c =
    typeof crypto !== 'undefined'
      ? (crypto as unknown as { randomUUID?: () => string })
      : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `t_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}
